import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Home, Users, Clock, MessageSquare, GraduationCap,
  CheckSquare, FileText, Calculator, PieChart, Settings,
  UserPlus, X, AlignJustify, ArrowDown, School, CalendarDays,
  ClipboardList, UserCog, ListOrdered, Briefcase, BarChart2, Globe,
  ChevronsLeft, ChevronsRight, PanelLeftClose, PanelLeftOpen, Network, LayoutDashboard, ClipboardCheck,
  UtensilsCrossed, Package
} from 'lucide-react';
import { usePermissions } from '../hooks/usePermissions';
import { useFeatures } from '../contexts/FeaturesContext';
import { clesLicence } from '../config/licenceModules';
import { Switch } from '../components/ui/switch';
import { accesFamille } from '../components/accesFamille/apiAccesFamille';

// ============================================================================
// HELPERS D'ANCRAGE - tooltips et panneaux en mode reduit
// ============================================================================
const clamp = (value, min, max) => Math.min(Math.max(value, min), max);

const getViewportHeight = () => (typeof window === 'undefined' ? 800 : window.innerHeight);

const SidebarTooltip = ({ text, anchorRect }) => {
  if (!anchorRect) {
    return null;
  }

  const top = clamp(anchorRect.top + anchorRect.height / 2, 28, getViewportHeight() - 28);

  return (
    <div
      className="tooltip-pop"
      style={{
        position: 'fixed',
        left: (anchorRect.right + 12) + 'px',
        top: top + 'px',
        padding: '7px 14px',
        borderRadius: '10px',
        background: 'linear-gradient(135deg, #319190, #3da8a7)',
        color: '#fff',
        fontSize: '12.5px',
        fontWeight: '600',
        whiteSpace: 'nowrap',
        zIndex: 9999,
        boxShadow: '0 4px 24px rgba(49,145,144,0.35), 0 0 0 1px rgba(255,255,255,0.12)',
        pointerEvents: 'none'
      }}
    >
      <div style={{
        position: 'absolute',
        left: '-5px',
        top: '50%',
        transform: 'translateY(-50%) rotate(45deg)',
        width: '10px',
        height: '10px',
        background: '#319190',
        borderRadius: '2px'
      }} />
      {text}
    </div>
  );
};

// ============================================================================
// MENU BUTTON - avec tooltip ancre en mode reduit
// ============================================================================
const MenuButton = ({
  icon: Icon,
  text,
  onClick,
  isActive,
  isCollapsed,
  hasSubmenu = false,
  isSubmenuOpen = false,
  submenuId = '',
  onCollapsedTooltipOpen,
  onCollapsedTooltipClose,
  onCollapsedSubmenuOpen
}) => {
  const openCollapsedOverlay = (event) => {
    if (!isCollapsed) {
      return;
    }

    if (hasSubmenu && onCollapsedSubmenuOpen) {
      onCollapsedSubmenuOpen(event);
      return;
    }

    onCollapsedTooltipOpen?.(event, text);
  };

  const closeCollapsedOverlay = () => {
    if (!isCollapsed || hasSubmenu) {
      return;
    }

    onCollapsedTooltipClose?.();
  };

  const tooltipHandlers = isCollapsed ? {
    onMouseEnter: openCollapsedOverlay,
    onMouseLeave: closeCollapsedOverlay,
    onFocus: openCollapsedOverlay,
    onBlur: closeCollapsedOverlay
  } : {};

  const buttonClasses = [
    'w-full group flex items-center gap-4 rounded-xl',
    'transition-all duration-500 relative overflow-hidden',
    isActive ? 'bg-white/20 text-white shadow-lg shadow-white/10' : 'text-white/80 hover:text-white hover:bg-white/10',
    'backdrop-blur-sm',
    hasSubmenu && !isCollapsed ? 'pr-10' : '',
    isCollapsed ? 'h-11 justify-center px-0' : 'px-4 py-3'
  ].filter(Boolean).join(' ');

  const iconClasses = [
    'relative flex items-center justify-center',
    'transition-all duration-500',
    isActive ? 'scale-110' : 'group-hover:scale-110'
  ].join(' ');

  const iconBlurClasses = [
    'absolute inset-0 blur-md',
    'transition-opacity duration-500',
    isActive ? 'opacity-50' : 'opacity-0 group-hover:opacity-30'
  ].join(' ');

  return (
    <div className="relative">
      <button
        onClick={(event) => {
          onCollapsedTooltipClose?.();
          onClick?.(event);
        }}
        aria-haspopup={hasSubmenu ? 'menu' : undefined}
        aria-expanded={hasSubmenu ? isSubmenuOpen : undefined}
        data-sidebar-trigger={submenuId || undefined}
        className={buttonClasses}
        {...tooltipHandlers}
      >
        {/* Effet de brillance au survol */}
        <div className="
          absolute inset-0
          bg-gradient-to-r from-transparent via-white/20 to-transparent
          -translate-x-[150%] group-hover:translate-x-[150%]
          transition-transform duration-1000
        "/>

        {/* Effet de bordure lumineuse */}
        {isActive && (
          <div className="
            absolute inset-0 -z-10
            bg-gradient-to-r from-white/20 to-white/5
            animate-pulse
          "/>
        )}

        {/* Icone avec effets */}
        <div className={iconClasses}>
          <Icon size={20} className="relative z-10" />
          <div className={iconBlurClasses}>
            <Icon size={20} />
          </div>
        </div>

        {!isCollapsed && (
          <>
            <span className="
              relative font-medium text-sm
              after:absolute after:bottom-0 after:left-0 after:h-px
              after:bg-white/50 after:w-0 after:transition-all after:duration-500
              group-hover:after:w-full
            ">{text}</span>

            {hasSubmenu && (
              <div className={['absolute right-4 top-1/2 -translate-y-1/2 transition-transform duration-500', isSubmenuOpen ? 'rotate-180' : ''].filter(Boolean).join(' ')}>
                <ArrowDown size={14} className="opacity-60" />
              </div>
            )}
          </>
        )}

        {isCollapsed && hasSubmenu && (
          <span className="absolute right-2 bottom-2 h-1.5 w-1.5 rounded-full bg-white/70 shadow-sm" />
        )}
      </button>
    </div>
  );
};
// ============================================================================
// SUB MENU BUTTON
// ============================================================================
/**
 * La pastille d'un item de menu : un nombre a cote du libelle.
 *
 * Arrondi de bouton, jamais de pilule (decision du 07/10/2026), et un
 * texte pour lecteur d'ecran : un nombre nu ne dit pas ce qu'il compte.
 */
const PastilleMenu = ({ nombre, quoi }) => {
  if (!nombre) return null;
  return (
    <span className="ml-auto inline-flex h-5 min-w-[20px] shrink-0 items-center justify-center rounded-lg bg-white px-1.5 text-[10px] font-bold tabular-nums text-[#246990]">
      {nombre > 99 ? '99+' : nombre}
      <span className="sr-only"> {quoi}</span>
    </span>
  );
};

const SubMenuButton = ({ icon: Icon, text, onClick, isActive, pastille, pastilleTexte }) => (
  <button
    onClick={onClick}
    className={`
      w-full flex items-center gap-3 px-4 py-2.5
      transition-all duration-500 relative
      rounded-xl overflow-hidden
      ${isActive
        ? 'bg-white/10 text-white'
        : 'text-white/70 hover:text-white hover:bg-white/5'
      }
      backdrop-blur-[2px]
      group
    `}
  >
    <div className="
      absolute inset-0
      bg-gradient-to-r from-transparent via-white/10 to-transparent
      -translate-x-[150%] group-hover:translate-x-[150%]
      transition-transform duration-1000
    "/>
    <div className="relative flex items-center gap-3 pl-8">
      <div className={`
        relative flex items-center justify-center
        transition-transform duration-300
        ${isActive ? 'scale-110' : 'group-hover:scale-110'}
      `}>
        <Icon size={16} />
      </div>
      <span className="text-sm relative">
        {text}
        <span className="
          absolute bottom-0 left-0 w-0 h-px
          bg-white/30 group-hover:w-full
          transition-all duration-500
        "/>
      </span>
      <PastilleMenu nombre={pastille} quoi={pastilleTexte} />
    </div>
  </button>
);

// ============================================================================
// FLYOUT DES SOUS-MODULES - mode reduit
// ============================================================================
const CollapsedSubmenuFlyout = ({ title, items, anchorRect, onClose }) => {
  if (!anchorRect) {
    return null;
  }

  const estimatedHeight = 58 + (items.length * 48) + 18;
  const viewportHeight = getViewportHeight();
  const top = clamp(anchorRect.top - 8, 12, Math.max(12, viewportHeight - estimatedHeight - 12));
  const arrowTop = clamp(anchorRect.top + anchorRect.height / 2 - top, 20, Math.max(20, estimatedHeight - 20));

  return (
    <div
      data-sidebar-flyout="true"
      className="sidebar-flyout-pop rounded-xl border border-white/15 bg-[#246990] text-white shadow-2xl shadow-black/25"
      style={{
        position: 'fixed',
        left: (anchorRect.right + 12) + 'px',
        top: top + 'px',
        width: '260px',
        zIndex: 9998
      }}
    >
      <div style={{
        position: 'absolute',
        left: '-6px',
        top: arrowTop + 'px',
        transform: 'rotate(45deg)',
        width: '12px',
        height: '12px',
        background: '#246990',
        borderLeft: '1px solid rgba(255,255,255,0.15)',
        borderBottom: '1px solid rgba(255,255,255,0.15)',
        borderRadius: '2px'
      }} />

      <div className="px-4 pt-3 pb-2">
        <p className="text-sm font-semibold">{title}</p>
      </div>

      <div className="custom-scrollbar space-y-1 overflow-y-auto p-2 pt-1" style={{ maxHeight: 'calc(100vh - 84px)' }}>
        {items.map(({ icon: ItemIcon, text, onClick: handleItemClick, isActive, pastille, pastilleTexte }) => {
          const itemClasses = [
            'w-full flex items-center gap-3 rounded-lg px-3 py-2.5 text-left text-sm transition-all duration-300 group',
            isActive ? 'bg-white/15 text-white' : 'text-white/75 hover:bg-white/10 hover:text-white'
          ].join(' ');

          return (
            <button
              key={text}
              type="button"
              className={itemClasses}
              onClick={() => {
                onClose();
                handleItemClick();
              }}
            >
              <ItemIcon size={17} className="flex-shrink-0 transition-transform duration-300 group-hover:scale-110" />
              <span className="min-w-0 truncate font-medium">{text}</span>
              <PastilleMenu nombre={pastille} quoi={pastilleTexte} />
            </button>
          );
        })}
      </div>
    </div>
  );
};

// ============================================================================
// ACTION BUTTON - footer avec tooltip harmonise
// ============================================================================
const SidebarActionButton = ({
  icon: Icon,
  label,
  onClick,
  isCollapsed,
  isActive = false,
  expandedLabel = '',
  iconSize = 20,
  onCollapsedTooltipOpen,
  onCollapsedTooltipClose
}) => {
  const openCollapsedTooltip = (event) => {
    if (isCollapsed) {
      onCollapsedTooltipOpen?.(event, label);
    }
  };

  const closeCollapsedTooltip = () => {
    if (isCollapsed) {
      onCollapsedTooltipClose?.();
    }
  };

  const tooltipHandlers = isCollapsed ? {
    onMouseEnter: openCollapsedTooltip,
    onMouseLeave: closeCollapsedTooltip,
    onFocus: openCollapsedTooltip,
    onBlur: closeCollapsedTooltip
  } : {};

  const wrapperClasses = isCollapsed ? 'relative w-full' : (expandedLabel ? 'relative flex-1' : 'relative w-11 flex-shrink-0');
  const buttonClasses = [
    'w-full flex items-center gap-3 rounded-xl transition-all duration-300 group',
    'bg-white/5 hover:bg-white/15 border border-white/10 hover:border-white/25',
    'text-white/60 hover:text-white',
    isCollapsed ? 'h-11 justify-center px-0' : (expandedLabel ? 'justify-start px-4 py-3' : 'h-11 justify-center'),
    isActive ? 'bg-white/15 text-white border-white/25' : ''
  ].filter(Boolean).join(' ');

  return (
    <div className={wrapperClasses}>
      <button
        type="button"
        onClick={(event) => {
          onCollapsedTooltipClose?.();
          onClick?.(event);
        }}
        aria-label={label}
        className={buttonClasses}
        {...tooltipHandlers}
      >
        <div className="transition-transform duration-300 group-hover:scale-110">
          <Icon size={iconSize} />
        </div>
        {!isCollapsed && expandedLabel && (
          <span className="text-sm font-medium">{expandedLabel}</span>
        )}
      </button>
    </div>
  );
};
// ============================================================================
// SECTION TITLE — avec icône indicatrice en mode plié
// ============================================================================
const SectionTitle = ({ text, isCollapsed }) => {
  if (isCollapsed) {
    return (
      <div className="flex justify-center py-3">
        <div className="w-6 h-px bg-white/20 rounded-full" />
      </div>
    );
  }
  return (
    <div className="px-4 py-3">
      <div className="flex items-center gap-2 group">
        <span className="text-xs font-medium tracking-wider text-white/50 uppercase">{text}</span>
        <div className="h-px flex-1 bg-gradient-to-r from-white/20 to-transparent"/>
      </div>
    </div>
  );
};

// ============================================================================
// SIDEBAR PRINCIPAL
// ============================================================================
const SidebarNav = ({
  isNavCollapsed,
  setIsNavCollapsed,
  sidebarDefaultCollapsed = false,
  onSidebarDefaultCollapsedChange = () => {},
  sidebarPreferenceSaving = false,
  sidebarPreferenceError = ''
}) => {
  const {
    hasPermission: aLaPermission,
    loading,
    permissions,
    detailedPermissions = []
  } = usePermissions();
  const { auMoinsUne, chargement: chargementLicence } = useFeatures();

  // Composition licence x droit.
  //
  // Plutot que de doubler chacun des tests du menu, on enveloppe hasPermission
  // une fois : une entree n'apparait que si l'ecole a souscrit le module ET que
  // l'employe a le droit d'y entrer. Une entree ajoutee demain herite du meme
  // comportement sans qu'on ait a y penser.
  //
  // Un module absent de LICENCE_PAR_MODULE releve du socle : jamais filtre.
  const moduleSousLicence = useCallback((module) => {
    const cles = clesLicence(module);
    return cles.length === 0 || auMoinsUne(cles);
  }, [auMoinsUne]);

  const hasPermission = useCallback(
    (module) => aLaPermission(module) && moduleSousLicence(module),
    [aLaPermission, moduleSousLicence]
  );

  const hasAnyPermission = useCallback(
    (modules) => modules.some((m) => hasPermission(m)),
    [hasPermission]
  );

  const navigate = useNavigate();
  const location = useLocation();
  const [showDossierMenu, setShowDossierMenu] = useState(
    location.pathname.includes('/dossier-eleves')
  );
  const [showSidebarPreference, setShowSidebarPreference] = useState(false);
  const [collapsedSubmenu, setCollapsedSubmenu] = useState(null);
  const [activeTooltip, setActiveTooltip] = useState(null);
  // Les conversations de familles non lues, pour la pastille du menu.
  const [messagesNonLus, setMessagesNonLus] = useState(0);

  const studentMenuLabel = '\u00c9l\u00e8ves';
  // Pas de raccourci ALL_MODULES : le serveur développe le passe-partout dans
  // la liste et en retire les refus individuels. Le rétablir ici rendrait une
  // dérogation « refuser » sans effet sur le menu.
  const hasPermissionKey = (permissionKeys) => {
    const keys = Array.isArray(permissionKeys) ? permissionKeys : [permissionKeys];
    return detailedPermissions.some((permission) => (
      keys.includes(permission.permission_key || permission.key)
    ));
  };

  // ⚠️ L'appel du compte est tenu par la MEME condition que l'entree de menu :
  // licence du module parents ET permission de repondre. Sans cela, chaque
  // ecole qui n'a pas le module prendrait un refus a chaque montage de la
  // coquille - et ce composant est traverse par tous les modules.
  const messagesFamillesOuverts = auMoinsUne(['module.parents'])
    && hasPermissionKey(['ELEVES.ACCES_FAMILLE.REPONDRE']);

  useEffect(() => {
    if (!messagesFamillesOuverts) {
      setMessagesNonLus(0);
      return undefined;
    }

    let vivant = true;
    const lire = async () => {
      try {
        const reponse = await accesFamille.nonLus();
        if (vivant) setMessagesNonLus(Number(reponse?.data?.non_lus || 0));
      } catch (e) {
        // Pas de pastille vaut mieux qu'un menu casse : la route rattrape
        // deja le cas d'une ecole non migree, et le reste ne doit pas
        // empecher la navigation.
      }
    };

    lire();
    const battement = setInterval(() => {
      if (document.visibilityState === 'visible') lire();
    }, 60000);

    return () => {
      vivant = false;
      clearInterval(battement);
    };
  }, [messagesFamillesOuverts]);
  // Suivi des enseignants : la porte du module (SUIVI_ENSEIGNANTS.ACCESS, par
  // le groupe — celle que la route exige), la licence (comprise dans
  // hasPermission) ET la clé fine SUIVI_ENSEIGNANTS.VUE_GLOBALE.VIEW, par le
  // rôle — même composition que « Messages des familles » (porte ELEVES + clé
  // ELEVES.ACCES_FAMILLE.REPONDRE).
  //
  // L'ancien test ouvrait l'entrée sur SUIVI_ENSEIGNANTS.ACCESS SEUL : la porte
  // du groupe Enseignant aurait suffi à montrer l'écran de la direction à tous
  // les enseignants. La porte seule ne suffit plus ; la clé fine seule non plus,
  // sinon l'entrée mènerait à l'écran « Accès refusé » de la route.
  const peutSuivreEnseignants = hasPermission('SUIVI_ENSEIGNANTS')
    && hasPermissionKey(['SUIVI_ENSEIGNANTS.VUE_GLOBALE.VIEW']);

  // Deux sous-entrees du dossier eleves relevent d'un autre module vendu :
  // l'emploi du temps et la vie scolaire. Une ecole peut avoir les eleves sans
  // avoir souscrit l'un ou l'autre.
  const dossierElevesSubmenuItems = [
    {
      icon: UserPlus,
      text: 'Admissions',
      onClick: () => navigate('/administration/dossier-eleves/inscription'),
      isActive: location.pathname.includes('/inscription')
    },
    {
      icon: Users,
      text: 'Liste des \u00e9l\u00e8ves',
      onClick: () => navigate('/administration/dossier-eleves/liste'),
      isActive: location.pathname.includes('/liste')
    },
    ...(auMoinsUne(['module.emploi_du_temps']) ? [{
      icon: CalendarDays,
      text: 'Emploi du temps',
      onClick: () => navigate('/administration/emploi-du-temps'),
      isActive: location.pathname.includes('/emploi-du-temps')
    }] : []),
    ...(auMoinsUne(['module.vie_scolaire']) ? [{
      icon: ClipboardList,
      text: 'Vie scolaire',
      onClick: () => navigate('/administration/disciplinaire'),
      isActive: location.pathname.includes('/disciplinaire')
    }] : []),
    // Espace famille : les messages des parents, pour qui a le droit d'y répondre.
    ...(messagesFamillesOuverts ? [{
      icon: MessageSquare,
      text: 'Messages des familles',
      onClick: () => navigate('/administration/messages-familles'),
      isActive: location.pathname.includes('/messages-familles'),
      pastille: messagesNonLus,
      pastilleTexte: `conversation${messagesNonLus > 1 ? 's' : ''} non lue${messagesNonLus > 1 ? 's' : ''}`
    }] : [])
  ];

  const closeCollapsedTooltip = () => setActiveTooltip(null);
  const closeCollapsedSubmenu = () => setCollapsedSubmenu(null);

  const closeCollapsedOverlays = () => {
    setActiveTooltip(null);
    setCollapsedSubmenu(null);
  };

  const showCollapsedTooltip = (event, text) => {
    setCollapsedSubmenu(null);
    setShowSidebarPreference(false);
    setActiveTooltip({
      text,
      anchorRect: event.currentTarget.getBoundingClientRect()
    });
  };

  const openCollapsedSubmenu = (event, id, title, items) => {
    setActiveTooltip(null);
    setShowSidebarPreference(false);
    setCollapsedSubmenu({
      id,
      title,
      items,
      anchorRect: event.currentTarget.getBoundingClientRect()
    });
  };

  const collapsedTooltipProps = {
    onCollapsedTooltipOpen: showCollapsedTooltip,
    onCollapsedTooltipClose: closeCollapsedTooltip
  };

  useEffect(() => {
    setActiveTooltip(null);
    if (!isNavCollapsed) {
      setCollapsedSubmenu(null);
    }
  }, [isNavCollapsed]);

  useEffect(() => {
    closeCollapsedOverlays();
  }, [location.pathname]);

  useEffect(() => {
    if ((!activeTooltip && !collapsedSubmenu) || typeof window === 'undefined') {
      return undefined;
    }

    const handleLayoutChange = () => closeCollapsedOverlays();
    window.addEventListener('scroll', handleLayoutChange, true);
    window.addEventListener('resize', handleLayoutChange);

    return () => {
      window.removeEventListener('scroll', handleLayoutChange, true);
      window.removeEventListener('resize', handleLayoutChange);
    };
  }, [activeTooltip, collapsedSubmenu]);

  useEffect(() => {
    if (!collapsedSubmenu || typeof document === 'undefined') {
      return undefined;
    }

    const handleMouseDown = (event) => {
      if (!(event.target instanceof Element)) {
        return;
      }

      const triggerSelector = '[data-sidebar-trigger="' + collapsedSubmenu.id + '"]';
      if (event.target.closest('[data-sidebar-flyout="true"]') || event.target.closest(triggerSelector)) {
        return;
      }

      setCollapsedSubmenu(null);
    };

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        setCollapsedSubmenu(null);
      }
    };

    document.addEventListener('mousedown', handleMouseDown);
    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('mousedown', handleMouseDown);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [collapsedSubmenu]);

  useEffect(() => {
    console.log('🔍 [SidebarNav] État des permissions:', {
      loading,
      permissions: Array.from(permissions || []),
      hasComptabilite: hasPermission('COMPTABILITE_ET_PAIE'),
      permissionsSize: permissions ? permissions.size : 0
    });
  }, [permissions, loading]);

  // Attendre les permissions ET la licence.
  //
  // Les permissions arrivent du cache local, donc instantanement ; la licence
  // vient du reseau. Ne surveiller que `loading` faisait rendre le menu complet
  // pendant la fraction de seconde precedant la reponse, puis en retirer une
  // dizaine d'entrees sous les yeux de l'utilisateur. Un menu qui se retracte
  // ressemble a un bug, et montre au passage des modules non souscrits.
  if (loading || chargementLicence) {
    return (
      <aside className={`
        ${isNavCollapsed ? 'w-[72px]' : 'w-72'}
        h-full
        transition-all duration-500 ease-out
        fixed left-0 top-0 bottom-0
        backdrop-blur-md
        z-50
      `}>
        <div className="absolute inset-0 bg-gradient-to-b from-[#319190]/90 to-[#63b5b4]/90"/>
        <div className="flex items-center justify-center h-full">
          <div className="text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-white mx-auto"></div>
            <p className="mt-2 text-white/70 text-sm">Chargement...</p>
          </div>
        </div>
      </aside>
    );
  }

  return (
    <aside
      className={`
        ${isNavCollapsed ? 'w-[72px]' : 'w-72'}
        h-full
        transition-all duration-500 ease-out
        fixed left-0 top-0 bottom-0
        backdrop-blur-md
        z-50
      `}
      style={{ overflow: 'visible' }}
    >
      {/* Background with blur and gradient */}
      <div className="absolute inset-0 bg-gradient-to-b from-[#319190]/90 to-[#63b5b4]/90"/>

      {/* Animated patterns */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_right,rgba(255,255,255,0.1)_0%,transparent_50%)]"/>
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_bottom_left,rgba(255,255,255,0.1)_0%,transparent_50%)]"/>
      </div>

      {/* Content */}
      <div className="relative h-full flex flex-col">

        {/* ── Header ── */}
        <div className={`h-20 flex items-center ${isNavCollapsed ? 'justify-center' : 'justify-between'} px-4 border-b border-white/10`}>
          {!isNavCollapsed && (
            <div className="flex items-center gap-3">
              <div className="
                w-12 h-12 rounded-xl
                bg-white/10 backdrop-blur-md
                flex items-center justify-center
                shadow-lg shadow-black/5
                border border-white/20
              ">

              </div>
            </div>
          )}
        </div>

        {/* ── Navigation ── */}
        <div
          className="flex-1 min-h-0 overflow-y-auto custom-scrollbar pt-4 px-2 pb-3"
          onScroll={() => {
            closeCollapsedOverlays();
          }}
        >
          {/* Tableau de bord — toujours visible */}
          <div className="space-y-1">
            <MenuButton
              icon={LayoutDashboard}
              text="Tableau de bord"
              onClick={() => navigate('/administration')}
              isActive={location.pathname === '/administration'}
              isCollapsed={isNavCollapsed}
                  {...collapsedTooltipProps}
            />
          </div>

          {/* Gestion Scolaire - ELEVES + CANTINE */}
          {hasAnyPermission(['ELEVES', 'CANTINE']) && (
            <div>
              <SectionTitle text="Gestion scolaire" isCollapsed={isNavCollapsed} />
              <div className="space-y-1">
                {hasPermission('ELEVES') && (
                <>
                <MenuButton
                  icon={Users}
                  text={studentMenuLabel}
                  onClick={(event) => {
                    if (isNavCollapsed) {
                      openCollapsedSubmenu(
                        event,
                        'dossier-eleves',
                        studentMenuLabel,
                        dossierElevesSubmenuItems
                      );
                    } else {
                      setShowDossierMenu(!showDossierMenu);
                    }
                  }}
                  isActive={showDossierMenu || location.pathname.includes('/dossier-eleves')}
                  isCollapsed={isNavCollapsed}
                  {...collapsedTooltipProps}
                  hasSubmenu
                  isSubmenuOpen={isNavCollapsed ? collapsedSubmenu?.id === 'dossier-eleves' : showDossierMenu}
                  submenuId="dossier-eleves"
                  onCollapsedSubmenuOpen={(event) => openCollapsedSubmenu(
                    event,
                    'dossier-eleves',
                    studentMenuLabel,
                    dossierElevesSubmenuItems
                  )}
                />

                {/* Rendu depuis dossierElevesSubmenuItems, la meme source que le
                    menu reduit : les deux vues ne peuvent plus diverger, et le
                    filtrage par licence n'est ecrit qu'une fois. */}
                {showDossierMenu && !isNavCollapsed && (
                  <div className="ml-4 space-y-1">
                    {dossierElevesSubmenuItems.map((item) => (
                      <SubMenuButton
                        key={item.text}
                        icon={item.icon}
                        text={item.text}
                        onClick={item.onClick}
                        isActive={item.isActive}
                      />
                    ))}
                  </div>
                )}
                </>
                )}

                {hasPermission('CANTINE') && (
                  <MenuButton
                    icon={UtensilsCrossed}
                    text="Cantine scolaire"
                    onClick={() => navigate('/administration/cantine')}
                    isActive={location.pathname.includes('/cantine')}
                    isCollapsed={isNavCollapsed}
                    {...collapsedTooltipProps}
                  />
                )}
              </div>
            </div>
          )}

          {/* Ressources Humaines */}
          {hasPermission('RESSOURCE_HUMAINES') && (
            <div>
              <SectionTitle text="Gestion du personnel" isCollapsed={isNavCollapsed} />
              <div className="space-y-1">
                <MenuButton
                  icon={UserCog}
                  text="Ressources humaines"
                  onClick={() => navigate('/administration/ressources-humaines')}
                  isActive={location.pathname.includes('/ressources-humaines')}
                  isCollapsed={isNavCollapsed}
                  {...collapsedTooltipProps}
                />
              </div>
            </div>
          )}

          {/* Enseignants */}
          {peutSuivreEnseignants && (
            <div>
              <SectionTitle text="Enseignants" isCollapsed={isNavCollapsed} />
              <div className="space-y-1">
                <MenuButton
                  icon={ClipboardCheck}
                  text="Suivi des enseignants"
                  onClick={() => navigate('/administration/enseignants')}
                  isActive={location.pathname.includes('/administration/enseignants')}
                  isCollapsed={isNavCollapsed}
                  {...collapsedTooltipProps}
                />
                {/* « Portail tablette » (/portail-enseignant) retiré : ses écritures
                    détruiraient les données de l'espace enseignant, qui le remplace,
                    et il n'a jamais servi en production. */}
              </div>
            </div>
          )}
          {/* Gestion Pédagogique */}
          {hasAnyPermission(['CLASSE_ET_MATIERES', 'CARNET_EVALUATION']) && (
            <div>
              <SectionTitle text="Gestion pédagogique" isCollapsed={isNavCollapsed} />
              <div className="space-y-1">
                {hasPermission('CLASSE_ET_MATIERES') && (
                  <MenuButton
                    icon={School}
                    text="Classes & Matières"
                    onClick={() => navigate('/administration/classe-et-matieres')}
                    isActive={location.pathname.includes('/classe-et-matieres')}
                    isCollapsed={isNavCollapsed}
                  {...collapsedTooltipProps}
                  />
                )}
                {hasPermission('CARNET_EVALUATION') && (
                  <MenuButton
                    icon={FileText}
                    text="Carnet d'évaluations"
                    onClick={() => navigate('/administration/notes')}
                    isActive={location.pathname.includes('/notes')}
                    isCollapsed={isNavCollapsed}
                  {...collapsedTooltipProps}
                  />
                )}
              </div>
            </div>
          )}

          {/* Gestion Financière */}
          {hasPermission('COMPTABILITE_ET_PAIE') && (
            <div>
              <SectionTitle text="Gestion financière" isCollapsed={isNavCollapsed} />
              <div className="space-y-1">
                <MenuButton
                  icon={Calculator}
                  text="Comptabilité & Paie"
                  onClick={() => navigate('/administration/comptabilite?tab=dashboard')}
                  isActive={location.pathname.includes('/comptabilite')}
                  isCollapsed={isNavCollapsed}
                  {...collapsedTooltipProps}
                />
              </div>
            </div>
          )}

          {/* Examens nationaux */}
          {hasPermission('INSCRIPTION_AUX_EXAMENS') && (
            <div>
              <SectionTitle text="Examens nationaux" isCollapsed={isNavCollapsed} />
              <div className="space-y-1">
                <MenuButton
                  icon={GraduationCap}
                  text="Inscription aux examens"
                  onClick={() => navigate('/administration/examens')}
                  isActive={location.pathname.includes('/examens')}
                  isCollapsed={isNavCollapsed}
                  {...collapsedTooltipProps}
                />
              </div>
            </div>
          )}

          {/* Tâches Inter-Services
              Le module s'appelle TACHES en base — 22 permissions, 10 sous-modules,
              et 69 employés détiennent déjà TACHES.ACCESS. Le menu testait
              TACHES_INTER_SITE, qui n'existe nulle part : l'entrée ne pouvait donc
              s'afficher que pour les porteurs d'ALL_MODULES. */}
          {hasPermission('TACHES') && (
            <div>
              <SectionTitle text="Collaboration" isCollapsed={isNavCollapsed} />
              <div className="space-y-1">
                <MenuButton
                  icon={CheckSquare}
                  text="Tâches Inter-Services"
                  onClick={() => navigate('/administration/taches')}
                  isActive={location.pathname.includes('/taches')}
                  isCollapsed={isNavCollapsed}
                  {...collapsedTooltipProps}
                />
              </div>
            </div>
          )}

          {/* Liaison Inter-Sites */}
          {hasPermission('LIAISON_INTER_SITES') && (
            <div>
              <SectionTitle text="Multi-Sites" isCollapsed={isNavCollapsed} />
              <div className="space-y-1">
                <MenuButton
                  icon={Network}
                  text="Liaison Inter-Sites"
                  onClick={() => navigate('/administration/intersite')}
                  isActive={location.pathname.includes('/intersite')}
                  isCollapsed={isNavCollapsed}
                  {...collapsedTooltipProps}
                />
              </div>
            </div>
          )}

          {/* Affluence */}
          {hasPermission('TENDANCES_ET_ANALYSE') && (
            <div>
              <SectionTitle text="Affluence" isCollapsed={isNavCollapsed} />
              <div className="space-y-1">
                <MenuButton
                  icon={BarChart2}
                  text="Tendances & analyse"
                  onClick={() => navigate('/administration/affluence')}
                  isActive={location.pathname === '/administration/affluence'}
                  isCollapsed={isNavCollapsed}
                  {...collapsedTooltipProps}
                />
              </div>
            </div>
          )}

          {/* Logistique (le parc et les bus scolaires). La porte LOGISTIQUE et
              les licences du module, composées par hasPermission. L'entrée
              porte le titre de la page, « Logistique », comme les autres
              modules (décision de Daniel du 04/11/2026) ; la section suit la
              forme « Gestion ... » des autres sections. */}
          {hasPermission('LOGISTIQUE') && (
            <div>
              <SectionTitle text="Gestion logistique" isCollapsed={isNavCollapsed} />
              <div className="space-y-1">
                <MenuButton
                  icon={Package}
                  text="Logistique"
                  onClick={() => navigate('/administration/logistique')}
                  isActive={location.pathname.includes('/administration/logistique')}
                  isCollapsed={isNavCollapsed}
                  {...collapsedTooltipProps}
                />
              </div>
            </div>
          )}

          {/* Outils administratifs */}
          {hasPermission('OUTILS_ADMINISTRATIFS') && (
            <div>
              <SectionTitle text="Administration des opérations" isCollapsed={isNavCollapsed} />
              <div className="space-y-1">
                <MenuButton
                  icon={Briefcase}
                  text="Outils administratifs"
                  onClick={() => navigate('/administration/configuration')}
                  isActive={location.pathname.includes('/administration/configuration')}
                  isCollapsed={isNavCollapsed}
                  {...collapsedTooltipProps}
                />
              </div>
            </div>
          )}

          {/* Message permissions insuffisantes */}
          {!hasAnyPermission(['ELEVES', 'CANTINE', 'RESSOURCE_HUMAINES', 'CLASSE_ET_MATIERES', 'CARNET_EVALUATION', 'COMPTABILITE_ET_PAIE', 'INSCRIPTION_AUX_EXAMENS', 'TENDANCES_ET_ANALYSE', 'OUTILS_ADMINISTRATIFS', 'TACHES', 'LIAISON_INTER_SITES', 'LOGISTIQUE']) && !peutSuivreEnseignants && (
            <div className="px-4 py-8 text-center">
              <div className="text-white/40 text-4xl mb-4">🔒</div>
              {!isNavCollapsed && (
                <p className="text-white/60 text-sm">
                  Aucune permission détectée. Contactez votre administrateur.
                </p>
              )}
            </div>
          )}
        </div>

        {/* ══════════════════════════════════════════════════
            BOUTON TOGGLE — en bas de la sidebar
        ══════════════════════════════════════════════════ */}
        <div className={['relative flex-shrink-0 border-t border-white/10', isNavCollapsed ? 'pl-2 pr-[14px] py-3' : 'p-3'].join(' ')}>
          <div className={isNavCollapsed ? 'flex flex-col gap-2' : 'flex gap-2'}>
            <SidebarActionButton
              icon={isNavCollapsed ? PanelLeftOpen : PanelLeftClose}
              label={isNavCollapsed ? 'Ouvrir le menu' : 'R\u00e9duire le menu'}
              onClick={() => {
                closeCollapsedOverlays();
                setShowSidebarPreference(false);
                setIsNavCollapsed(!isNavCollapsed);
              }}
              isCollapsed={isNavCollapsed}
                  {...collapsedTooltipProps}
              expandedLabel={'R\u00e9duire le menu'}
            />

            <SidebarActionButton
              icon={Settings}
              label={'Pr\u00e9f\u00e9rence du menu'}
              onClick={() => {
                closeCollapsedOverlays();
                setShowSidebarPreference(prev => !prev);
              }}
              isCollapsed={isNavCollapsed}
                  {...collapsedTooltipProps}
              isActive={showSidebarPreference}
              iconSize={18}
            />
          </div>
          {showSidebarPreference && (
            <div className={`absolute bottom-full mb-3 z-[60] ${isNavCollapsed ? 'left-2 w-72' : 'left-3 right-3'} rounded-xl border border-white/15 bg-[#246990] p-4 text-white shadow-2xl shadow-black/20`}>
              <div className="mb-3">
                <p className="text-sm font-semibold">Préférence du menu</p>
                <p className="mt-1 text-xs text-white/70">Appliqué à ce compte utilisateur</p>
              </div>

              <div className="flex items-center justify-between gap-3 rounded-lg bg-white/10 px-3 py-3">
                <div>
                  <p className="text-sm font-medium">Menu réduit par défaut</p>
                  <p className="mt-0.5 text-xs text-white/65">À la prochaine connexion</p>
                </div>
                <Switch
                  checked={sidebarDefaultCollapsed}
                  onCheckedChange={onSidebarDefaultCollapsedChange}
                  disabled={sidebarPreferenceSaving}
                />
              </div>

              <div className="mt-3 min-h-[18px] text-xs text-white/70">
                {sidebarPreferenceSaving ? 'Enregistrement...' : sidebarPreferenceError || 'Enregistré'}
              </div>
            </div>
          )}
        </div>

        {isNavCollapsed && activeTooltip && (
          <SidebarTooltip
            text={activeTooltip.text}
            anchorRect={activeTooltip.anchorRect}
          />
        )}

        {isNavCollapsed && collapsedSubmenu && (
          <CollapsedSubmenuFlyout
            title={collapsedSubmenu.title}
            items={collapsedSubmenu.items}
            anchorRect={collapsedSubmenu.anchorRect}
            onClose={closeCollapsedSubmenu}
          />
        )}
      </div>

      <style jsx global>{`
        .custom-scrollbar::-webkit-scrollbar {
          width: 6px;
          background: transparent;
        }
        .custom-scrollbar::-webkit-scrollbar-track {
          background: transparent;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb {
          background: rgba(255, 255, 255, 0.2);
          border-radius: 3px;
          transition: background-color 0.3s;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb:hover {
          background: rgba(255, 255, 255, 0.3);
        }
        .custom-scrollbar {
          scrollbar-width: thin;
          scrollbar-color: rgba(255, 255, 255, 0.2) transparent;
        }
        .custom-scrollbar::-webkit-scrollbar-corner {
          background: transparent;
        }

        /* Tooltip animation */
        .tooltip-pop {
          animation: tooltipSlideIn 0.15s ease-out forwards;
        }
        @keyframes tooltipSlideIn {
          from { opacity: 0; transform: translate(-6px, -50%); }
          to   { opacity: 1; transform: translate(0, -50%); }
        }
        .sidebar-flyout-pop {
          animation: sidebarFlyoutIn 0.16s ease-out forwards;
        }
        @keyframes sidebarFlyoutIn {
          from { opacity: 0; transform: translateX(-8px); }
          to   { opacity: 1; transform: translateX(0); }
        }
      `}</style>
    </aside>
  );
};

export default SidebarNav;
